import { Stack, Box, Chip, Typography, IconButton } from '@mui/material';
import { CaretLeft, CaretRight } from '@phosphor-icons/react';
import * as React from 'react';
import useModifiableParams from '../../hooks/use-modifiable-params';
import {
  startOfToday,
  subDays,
  startOfMonth as dfStartOfMonth,
  endOfMonth as dfEndOfMonth,
  isSameMonth,
  addMonths,
  parseISO,
  format,
} from 'date-fns';

function isoDate(d: Date) {
  return format(d, 'yyyy-MM-dd');
}

function computeRange(timeframe: 'week' | 'month', anchorStart?: Date) {
  const today = startOfToday();
  if (timeframe === 'week') {
    const end = today;
    const start = subDays(end, 6);
    return { startDate: isoDate(start), endDate: isoDate(end) };
  }
  const anchor = anchorStart
    ? dfStartOfMonth(anchorStart)
    : dfStartOfMonth(today);
  const isCurrent = isSameMonth(anchor, today);
  const end = isCurrent ? today : dfEndOfMonth(anchor);
  return { startDate: isoDate(anchor), endDate: isoDate(end) };
}

export default function PeriodToggle() {
  const { getParams, setModifiedParams } = useModifiableParams({
    defaults: { timeframe: 'week' },
  });

  const params = getParams();
  const selectedPeriod = params.timeframe === 'month' ? 'Month' : 'Week';

  // Keep startDate/endDate in sync with timeframe selection
  React.useEffect(() => {
    const period = params.timeframe === 'month' ? 'month' : 'week';
    const anchor =
      period === 'month' && params.startDate
        ? parseISO(params.startDate)
        : undefined;
    const { startDate, endDate } = computeRange(period, anchor);
    if (params.startDate !== startDate || params.endDate !== endDate) {
      setModifiedParams({ startDate, endDate });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [params.timeframe]);

  const handlePeriodChange = (period: 'Week' | 'Month') => {
    const p = period.toLowerCase() as 'week' | 'month';
    const { startDate, endDate } = computeRange(p);
    setModifiedParams({ timeframe: p, startDate, endDate });
  };

  const anchorStart = React.useMemo(() => {
    if (params.timeframe !== 'month') return null as Date | null;
    const today = startOfToday();
    return params.startDate
      ? dfStartOfMonth(parseISO(params.startDate))
      : dfStartOfMonth(today);
  }, [params.timeframe, params.startDate]);

  const monthLabel = React.useMemo(() => {
    return anchorStart ? format(anchorStart, 'MMMM yyyy') : '';
  }, [anchorStart]);

  const canGoForward = React.useMemo(() => {
    if (!anchorStart) return false;
    return !isSameMonth(anchorStart, startOfToday());
  }, [anchorStart]);

  const goPrevMonth = () => {
    if (!anchorStart) return;
    const prev = dfStartOfMonth(addMonths(anchorStart, -1));
    const { startDate, endDate } = computeRange('month', prev);
    setModifiedParams({ startDate, endDate });
  };
  const goNextMonth = () => {
    if (!anchorStart) return;
    const next = dfStartOfMonth(addMonths(anchorStart, 1));
    const { startDate, endDate } = computeRange('month', next);
    setModifiedParams({ startDate, endDate });
  };

  return (
    <Stack spacing={1}>
      <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
        <Chip
          label="Week"
          onClick={() => handlePeriodChange('Week')}
          color={selectedPeriod === 'Week' ? 'primary' : 'default'}
          sx={{ py: '15px', px: '10px' }}
        />
        <Chip
          label="Month"
          onClick={() => handlePeriodChange('Month')}
          color={selectedPeriod === 'Month' ? 'primary' : 'default'}
          sx={{ py: '15px', px: '10px' }}
        />
        {selectedPeriod === 'Month' && anchorStart && (
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, ml: 1 }}>
            <IconButton size="small" onClick={goPrevMonth}>
              <CaretLeft size={18} />
            </IconButton>
            <Typography
              variant="body2"
              sx={{ minWidth: 140, textAlign: 'center' }}
            >
              {monthLabel}
            </Typography>
            {canGoForward && (
              <IconButton size="small" onClick={goNextMonth}>
                <CaretRight size={18} />
              </IconButton>
            )}
          </Box>
        )}
      </Box>

      <Typography variant="body2" color="text.secondary">
        Always displays data starting from today's date
      </Typography>
    </Stack>
  );
}
